import { useParams } from "@tanstack/solid-router";
import { For, Show, createEffect, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { CsvIoButtons } from "~/components/CsvIoButtons";
import { CustomFieldsRenderer } from "~/components/CustomFieldsRenderer";
import { ProductsBulkBar } from "~/components/ProductsBulkBar";
import { SavedViewsBar } from "~/components/SavedViewsBar";
import {
  useArchiveProduct,
  useCreateProduct,
  useProduct,
  useProducts,
  useUpdateProduct,
} from "~/lib/hooks";
import { useRowSelection } from "~/lib/selection";
import type { ProductCreate, ProductListItem, ProductUpdate } from "~/lib/types";

type EditState = { mode: "add" } | { mode: "edit"; productId: string };

type FormState = {
  name: string;
  sku: string;
  description: string;
  unit_price: string;
  currency: string;
  active: boolean;
  custom_fields: Record<string, unknown>;
};

const blankForm = (): FormState => ({
  name: "",
  sku: "",
  description: "",
  unit_price: "",
  currency: "",
  active: true,
  custom_fields: {},
});

function formatPrice(price: number | null, currency: string | null): string {
  if (price === null) return "—";
  return `${price.toFixed(2)} ${currency ?? ""}`.trim();
}

export function ProductsPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };

  const [search, setSearch] = createSignal("");
  const [includeArchived, setIncludeArchived] = createSignal(false);
  const [edit, setEdit] = createSignal<EditState | null>(null);

  const products = useProducts(
    () => params().wsKey,
    () => ({
      q: search() || undefined,
      include_archived: includeArchived() || undefined,
      limit: 100,
    }),
  );
  const archiveMutation = useArchiveProduct(() => params().wsKey);

  const visibleRows = () => products.data?.items ?? [];
  const selection = useRowSelection<ProductListItem>(() => visibleRows().map((r) => r.id));

  return (
    <AppShell wsKey={params().wsKey} pageTitle="Products">
      <div class="flex flex-col gap-4">
        <SavedViewsBar
          wsKey={() => params().wsKey}
          entityType={() => "product"}
          currentFilters={() => ({
            q: search() || undefined,
            include_archived: includeArchived() || undefined,
          })}
          onApply={(f) => {
            setSearch((f.q as string) ?? "");
            setIncludeArchived(Boolean(f.include_archived));
          }}
          onClear={() => {
            setSearch("");
            setIncludeArchived(false);
          }}
        />
        <div class="flex items-center gap-3 flex-wrap">
          <input
            type="search"
            class="input input-bordered input-sm w-72"
            placeholder="Search products or SKU…"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            aria-label="Search products"
          />
          <label class="label cursor-pointer gap-2">
            <input
              type="checkbox"
              class="checkbox checkbox-sm"
              checked={includeArchived()}
              onChange={(e) => setIncludeArchived(e.currentTarget.checked)}
            />
            <span class="label-text">Include archived</span>
          </label>
          <div class="flex-1" />
          <CsvIoButtons wsKey={() => params().wsKey} plural="products" />
          <button
            type="button"
            class="btn btn-primary btn-sm"
            onClick={() => setEdit({ mode: "add" })}
          >
            Add product
          </button>
        </div>

        <ProductsBulkBar
          wsKey={() => params().wsKey}
          count={selection.count}
          rows={() => selection.selectedRows(visibleRows())}
          onClear={selection.clear}
        />

        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-0">
            <div class="overflow-x-auto">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th class="w-8">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        aria-label="Select all"
                        checked={
                          visibleRows().length > 0 && selection.count() === visibleRows().length
                        }
                        onChange={(e) =>
                          e.currentTarget.checked ? selection.selectAll() : selection.clear()
                        }
                      />
                    </th>
                    <th>Name</th>
                    <th>SKU</th>
                    <th>Unit price</th>
                    <th>Active</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={visibleRows()}>
                    {(p) => (
                      <tr class={selection.has(p.id) ? "bg-base-200" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            class="checkbox checkbox-sm"
                            aria-label={`Select ${p.name}`}
                            checked={selection.has(p.id)}
                            onChange={() => selection.toggle(p.id)}
                          />
                        </td>
                        <td class="font-medium">{p.name}</td>
                        <td class="text-xs opacity-60">
                          <Show when={p.sku} fallback={<span class="opacity-50">—</span>}>
                            <code>{p.sku}</code>
                          </Show>
                        </td>
                        <td>{formatPrice(p.unit_price, p.currency)}</td>
                        <td>
                          <Show
                            when={p.active}
                            fallback={<span class="badge badge-ghost badge-sm">inactive</span>}
                          >
                            <span class="badge badge-success badge-sm">active</span>
                          </Show>
                        </td>
                        <td class="text-right whitespace-nowrap">
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs"
                            onClick={() => setEdit({ mode: "edit", productId: p.id })}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs text-error"
                            onClick={() =>
                              archiveMutation.mutate({
                                productId: p.id,
                                version: p.version,
                              })
                            }
                          >
                            Archive
                          </button>
                        </td>
                      </tr>
                    )}
                  </For>
                  <Show when={(products.data?.items.length ?? 0) === 0 && !products.isLoading}>
                    <tr>
                      <td colspan={6} class="text-center py-6 opacity-60 text-sm">
                        No products yet. Add one to build out your catalogue.
                      </td>
                    </tr>
                  </Show>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Show when={edit()}>
          {(state) => (
            <ProductEditModal
              wsKey={() => params().wsKey}
              state={state()}
              onClose={() => setEdit(null)}
            />
          )}
        </Show>
      </div>
    </AppShell>
  );
}

type ModalProps = {
  wsKey: () => string;
  state: EditState;
  onClose: () => void;
};

function ProductEditModal(props: ModalProps) {
  const createMutation = useCreateProduct(props.wsKey);
  const updateMutation = useUpdateProduct(props.wsKey);
  const [form, setForm] = createSignal<FormState>(blankForm());
  const [error, setError] = createSignal<string | null>(null);
  const [hydrated, setHydrated] = createSignal(props.state.mode === "add");

  // In edit mode, fetch the full ProductRead so description + custom_fields
  // hydrate the form (the list-item view omits both).
  const fetched = useProduct(props.wsKey, () =>
    props.state.mode === "edit" ? props.state.productId : null,
  );

  // Hydrate exactly once when the fetched product arrives — gate on
  // `hydrated` so subsequent refetches don't clobber user edits.
  createEffect(() => {
    if (hydrated() || !fetched.data) return;
    const p = fetched.data;
    setForm({
      name: p.name,
      sku: p.sku ?? "",
      description: p.description ?? "",
      unit_price: p.unit_price?.toString() ?? "",
      currency: p.currency ?? "",
      active: p.active,
      custom_fields: (p.custom_fields ?? {}) as Record<string, unknown>,
    });
    setHydrated(true);
  });

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const f = form();
    if (!f.name.trim()) return;
    const customFields = Object.keys(f.custom_fields).length > 0 ? f.custom_fields : null;
    try {
      if (props.state.mode === "add") {
        const payload: ProductCreate = {
          name: f.name.trim(),
          sku: f.sku.trim() || null,
          description: f.description.trim() || null,
          unit_price: f.unit_price ? Number(f.unit_price) : null,
          currency: f.currency.trim().toUpperCase() || null,
          active: f.active,
          custom_fields: customFields,
        };
        await createMutation.mutateAsync(payload);
      } else if (fetched.data) {
        const payload: ProductUpdate = {
          name: f.name.trim(),
          sku: f.sku.trim() || null,
          description: f.description.trim() || null,
          unit_price: f.unit_price ? Number(f.unit_price) : null,
          currency: f.currency.trim().toUpperCase() || null,
          active: f.active,
          custom_fields: customFields,
        };
        await updateMutation.mutateAsync({
          productId: fetched.data.id,
          body: payload,
          version: fetched.data.version,
        });
      }
      props.onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const title = () => (props.state.mode === "add" ? "Add product" : "Edit product");

  return (
    <dialog open class="modal modal-open" aria-modal="true">
      <div class="modal-box">
        <h3 class="font-bold text-lg mb-4">{title()}</h3>

        <Show
          when={hydrated()}
          fallback={
            <div role="status" class="text-center py-6 opacity-60">
              Loading…
            </div>
          }
        >
          <form onSubmit={handleSubmit} class="flex flex-col gap-3">
            <input
              class="input input-bordered"
              placeholder="Name"
              aria-label="Name"
              value={form().name}
              onInput={(e) => update("name", e.currentTarget.value)}
              required
            />
            <input
              class="input input-bordered"
              placeholder="SKU (optional)"
              aria-label="SKU"
              value={form().sku}
              onInput={(e) => update("sku", e.currentTarget.value)}
            />
            <textarea
              class="textarea textarea-bordered"
              placeholder="Description (optional)"
              aria-label="Description"
              value={form().description}
              onInput={(e) => update("description", e.currentTarget.value)}
            />
            <div class="grid grid-cols-2 gap-3">
              <input
                type="number"
                step="0.01"
                min="0"
                class="input input-bordered"
                placeholder="Unit price"
                aria-label="Unit price"
                value={form().unit_price}
                onInput={(e) => update("unit_price", e.currentTarget.value)}
              />
              <input
                class="input input-bordered uppercase"
                placeholder="USD"
                maxlength="3"
                aria-label="Currency"
                value={form().currency}
                onInput={(e) => update("currency", e.currentTarget.value)}
              />
            </div>
            <label class="label cursor-pointer justify-start gap-2">
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                checked={form().active}
                onChange={(e) => update("active", e.currentTarget.checked)}
              />
              <span class="label-text">Active (appears in pickers)</span>
            </label>

            <CustomFieldsRenderer
              wsKey={props.wsKey}
              entityType="product"
              values={() => form().custom_fields}
              onChange={(key, value) =>
                setForm((f) => {
                  const next = { ...f.custom_fields };
                  if (
                    value === null ||
                    value === "" ||
                    (Array.isArray(value) && value.length === 0)
                  ) {
                    delete next[key];
                  } else {
                    next[key] = value;
                  }
                  return { ...f, custom_fields: next };
                })
              }
            />

            <Show when={error()}>
              <div role="alert" class="alert alert-error text-sm">
                {error()}
              </div>
            </Show>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={props.onClose}>
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {props.state.mode === "add" ? "Add" : "Save"}
              </button>
            </div>
          </form>
        </Show>
      </div>
    </dialog>
  );
}
